<template>
  <div id="board">
    <div id="postcounter" class="postcounter">
      {{ postCount }} posts and counting!
    </div>
    <form v-on:submit.prevent="addPost">
      <input
        type="text"
        placeholder="Your post goes here..."
        v-model="newPost"
        class="post-box"
        maxlength="280"
      />
    </form>
    <div class="connection-text">
      Connection Status
      <img
        v-bind:class="[
          { 'live-Stream': isConnected },
          { 'dead-Stream': !isConnected },
        ]"
        src="../assets/red_circle_sm.png"
      />
    </div>
    <ul>
      <TransitionGroup name="list">
        <li v-for="(post, index) in posts" v-bind:key="post._id">
          <div class="message" v-bind:class="{ myMessage: post.myMessage }">
            <span class="timestamp">#{{ postCount - index }} {{ formatTime(post.posttime) }}</span>
            {{ post.msg }}
          </div>
        </li>
      </TransitionGroup>
      <div ref="scrollSentinel"></div>
    </ul>
  </div>
</template>
<script>
import { ref, onMounted, onUnmounted } from "vue";
import boardApi from "@/services/boardApi.js";
import { socket } from "@/utils/socket.js";

export default {
  setup() {
    const postCount = ref(0);
    const newPost = ref("");
    const posts = ref([]);
    const busy = ref(false);
    const hasMore = ref(true);
    const nextCursor = ref(null);
    const lastSubmitted = ref(0);
    const isConnected = ref(socket.connected);
    const scrollSentinel = ref(null);
    let observer = null;
    let retryTimer = null;
    let retriedCurrentPage = false;

    function formatTime(posttime) {
      if (!posttime) return "";
      const date = new Date(posttime);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleString([], {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    function addPost() {
      if (new Date().valueOf() - lastSubmitted.value > 2000) {
        socket.emit("addPost", newPost.value);
        newPost.value = "";
        lastSubmitted.value = new Date().valueOf();
      }
    }

    async function loadPosts() {
      const response = await boardApi.getPosts();
      const existingIds = new Set(posts.value.map((post) => post._id));
      posts.value.push(
        ...response.data.posts.filter((post) => !existingIds.has(post._id))
      );
      nextCursor.value = response.data.nextCursor;
      hasMore.value = response.data.hasMore;
    }

    async function loadPostCount() {
      const response = await boardApi.getPostCount();
      postCount.value = response.data;
    }

    function reobserveSentinel() {
      if (observer && scrollSentinel.value) {
        observer.unobserve(scrollSentinel.value);
        observer.observe(scrollSentinel.value);
      }
    }

    async function loadMore() {
      if (busy.value || !hasMore.value || posts.value.length === 0) return;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      busy.value = true;
      let shouldReobserve = false;

      try {
        const response = await boardApi.getPosts(nextCursor.value);

        const existingIds = new Set(posts.value.map((post) => post._id));
        posts.value.push(
          ...response.data.posts.filter((post) => !existingIds.has(post._id))
        );
        nextCursor.value = response.data.nextCursor;
        hasMore.value = response.data.hasMore;
        retriedCurrentPage = false;
        shouldReobserve = hasMore.value;
      } catch {
        // Retry one transient failure without creating an unbounded request loop.
        if (!retriedCurrentPage) {
          retriedCurrentPage = true;
          retryTimer = setTimeout(() => {
            retryTimer = null;
            reobserveSentinel();
          }, 1000);
        }
      } finally {
        busy.value = false;
      }

      if (shouldReobserve) {
        // Re-fire if the sentinel is still visible after appending the batch.
        reobserveSentinel();
      }
    }

    function onConnect() {
      isConnected.value = true;
    }

    function onDisconnect() {
      isConnected.value = false;
    }

    function onNewPost(data) {
      if (data.sessionId === socket.id) {
        data.myMessage = true;
      }
      posts.value.unshift(data);
      postCount.value++;
    }

    onMounted(async () => {
      socket.on("connect", onConnect);
      socket.on("disconnect", onDisconnect);
      socket.on("newPost", onNewPost);

      // Populate the list before observing its sentinel. Observing an empty
      // list can consume the initial intersection event before a cursor exists.
      loadPostCount().catch(() => {});

      try {
        await loadPosts();
      } catch {
        return;
      }

      if (scrollSentinel.value) {
        observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              loadMore();
            }
          },
          { rootMargin: "0px 0px 1200px 0px" }
        );
        observer.observe(scrollSentinel.value);
      }
    });

    onUnmounted(() => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("newPost", onNewPost);

      if (observer) {
        observer.disconnect();
      }
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    });

    return {
      postCount,
      newPost,
      posts,
      isConnected,
      scrollSentinel,
      formatTime,
      addPost,
    };
  },
};
</script>

<style lang="css">
ul,
li {
  list-style: none;
  margin: 5px;
  padding: 5px;
  text-align: center;
}
li {
  text-align: center;
}
.message {
  color: var(--text-color);
  margin-left: auto;
  margin-right: auto;
  clear: both;
  line-height: 18px;
  font-size: 15px;
  padding: 8px;
  position: relative;
  max-width: 60%;
  overflow-wrap: anywhere;
  word-break: break-word;
  overflow-x: hidden;
  white-space: pre-wrap;
  hyphens: auto;
  background: rgba(185, 185, 185, 0.089);
  border-radius: 5px;
  text-align: left;
}
.timestamp {
  position: absolute;
  top: 6px;
  right: 8px;
  color: #999;
  font-size: 11px;
  opacity: 0;
  transition: opacity 0.75s ease-in-out;
  transition-delay: 0s;
}
.message:hover .timestamp {
  opacity: 1;
  transition-delay: 1.5s;
}
.myMessage {
  background: rgba(144, 162, 221, 0.164);
}
.post-box {
  color: var(--post-box-text-color);
  clear: both;
  margin-top: 10px;
  border-radius: 5px;
  margin-left: auto;
  margin-right: auto;
  min-width: 20%;
  max-width: 40%;
  padding: 8px;
  border: rgba(207, 35, 35, 0.589);
  background: rgba(54, 69, 87, 0.089);
  outline: none;
  font-size: 15px;
  text-align: center;
  transition: all 0.5s;
}
::placeholder {
  color: rgba(141, 141, 141, 0.589);
}
.post-box:focus {
  background: rgba(144, 162, 221, 0.164);
  min-width: 60%;
  max-width: 60%;
  text-align: left;
}
.post-box:focus::placeholder {
  transition: opacity 0.5s;
  color: transparent;
}
::-webkit-scrollbar {
  display: none;
}
.connection-text {
  position: fixed;
  padding: 10px;
  bottom: 0;
  right: 16px;
  text-align: right;
  font-size: 11px;
}
.live-Stream {
  position: fixed;
  padding: 10px;
  bottom: 0;
  right: 0;
  width: 11px;
  filter: hue-rotate(120deg) saturate(350%);
}
.dead-Stream {
  position: fixed;
  padding: 10px;
  bottom: 0;
  right: 0;
  width: 11px;
  animation: blinker 1s linear infinite;
}
@keyframes blinker {
  50% {
    opacity: 0;
  }
}
.list-enter-active,
.list-leave-active,
.list-move {
  transition: 500ms cubic-bezier(0.59, 0.12, 0.34, 0.95);
  transition-property: opacity, transform;
}
.list-enter-from {
  opacity: 0;
  transform: translateY(-50px);
}

.list-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.list-leave-active {
  position: absolute;
}

.list-leave-to {
  opacity: 0;
  transform: scaleY(0);
  transform-origin: center;
}

@keyframes fadein {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
